import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { supabase, supabaseConfigured } from './supabase'
import { getSession, setSession } from './session'

/**
 * useAuth — the single source of truth for the dedicated auth pages.
 *
 * It reuses the EXACT Supabase + platform-token logic that used to live inside
 * AuthGate (exchange → POST /auth/google → hasWorkspace ? setSession :
 * POST /auth/google/workspace), so the login behaviour is unchanged. The only
 * differences from the old embedded flow:
 *   - on success it navigates to the `?next=` destination (default `/`) instead
 *     of flipping an internal phase, and
 *   - the password-reset redirect points at `/reset-password` (a real page).
 *
 * `mode` tunes the mount-time listeners:
 *   'signin' | 'signup' → exchange any established/returned Supabase session
 *   'reset'             → wait for the PASSWORD_RECOVERY session, don't exchange
 *   'forgot'            → no session handling (just sends the email)
 */

// Only allow same-origin, single-slash paths as a post-login destination
// (never an absolute URL / protocol-relative // — that would be an open redirect).
function safeNext(raw) {
  if (!raw || typeof raw !== 'string') return '/'
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/'
  return raw
}

export function useAuth({ mode }) {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = useMemo(() => safeNext(params.get('next')), [params])

  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)        // { tone, text } | null
  const [pending, setPending] = useState(null)      // { email, name, supabaseToken } — needs a workspace
  const [wsName, setWsName] = useState('')
  const [recoveryReady, setRecoveryReady] = useState(false)

  const configured = supabaseConfigured

  const guardSupabase = useCallback(() => {
    if (!supabase) { toast.error('Sign-in is not configured in this build.'); return false }
    return true
  }, [])

  const goNext = useCallback(() => navigate(next, { replace: true }), [navigate, next])

  // Turn a Supabase session into our platform session (or offer workspace creation).
  const exchange = useCallback(async (sb) => {
    if (!sb?.access_token || getSession()?.apiKey) return
    try {
      const { data } = await axios.post('/api/v1/auth/google', { supabaseToken: sb.access_token })
      if (data.hasWorkspace) {
        setSession({ apiKey: data.token, namespaceId: data.namespaceId, name: data.name, plan: data.plan })
        goNext()
      } else {
        setPending({ email: data.email, name: data.name, supabaseToken: sb.access_token })
        setWsName(data.name || '')
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Sign-in failed. Please try again.')
    }
  }, [goNext])

  // Already logged in (platform session) → never show an auth page. Bounce to next/`/`.
  useEffect(() => {
    if (getSession()?.apiKey) navigate(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Session listeners. signin/signup exchange; reset waits for recovery.
  useEffect(() => {
    if (!configured || !supabase) return

    supabase.auth.getSession().then(({ data }) => {
      const sess = data?.session
      if (mode === 'reset') { if (sess) setRecoveryReady(true); return }
      if (mode === 'forgot') return
      if (sess?.access_token && !getSession()?.apiKey) exchange(sess)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((event, sb) => {
      if (event === 'PASSWORD_RECOVERY') { setRecoveryReady(true); return }
      if (mode === 'reset' || mode === 'forgot') return
      if (getSession()?.apiKey) return
      if (sb?.access_token) exchange(sb)
    })

    return () => sub.subscription.unsubscribe()
  }, [mode, configured, exchange])

  // ── actions ────────────────────────────────────────────────────────────────

  // Return to THIS auth page (with its ?next=) after the Google round-trip so
  // exchange() can run and forward to the real destination.
  const signInGoogle = useCallback(() => {
    if (!guardSupabase()) return
    const redirectTo = window.location.origin + window.location.pathname + window.location.search
    supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo } })
  }, [guardSupabase])

  const signInEmail = useCallback(async ({ email, password }) => {
    if (!guardSupabase() || busy) return
    setBusy(true); setNotice(null)
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
    setBusy(false)
    if (error) toast.error(error.message)
    // success → onAuthStateChange → exchange() → goNext()
  }, [guardSupabase, busy])

  const signUpEmail = useCallback(async ({ name, email, password }) => {
    if (!guardSupabase() || busy) return
    setBusy(true); setNotice(null)
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { data: { full_name: name.trim() }, emailRedirectTo: window.location.origin },
    })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    if (!data.session) setNotice({ tone: 'success', text: 'Check your email to confirm your account, then sign in.' })
    // if confirmation is disabled, a session is returned → onAuthStateChange → exchange()
  }, [guardSupabase, busy])

  const sendReset = useCallback(async ({ email }) => {
    if (!guardSupabase() || busy) return
    setBusy(true); setNotice(null)
    const redirectTo = `${window.location.origin}/reset-password`
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    setNotice({ tone: 'success', text: 'If that email exists, a password-reset link is on its way.' })
  }, [guardSupabase, busy])

  const updatePassword = useCallback(async ({ password }) => {
    if (!guardSupabase() || busy) return
    setBusy(true)
    const { error } = await supabase.auth.updateUser({ password })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success('Password updated. Please sign in.')
    navigate('/signin', { replace: true })
  }, [guardSupabase, busy, navigate])

  const createWorkspace = useCallback(async () => {
    if (!pending || busy) return
    setBusy(true)
    try {
      const { data } = await axios.post('/api/v1/auth/google/workspace', {
        supabaseToken: pending.supabaseToken,
        name: wsName.trim() || pending.name,
      })
      setSession({
        apiKey: data.token, namespaceId: data.namespaceId, name: data.name, plan: data.plan,
        provisioning: data.isolationTier === 'provisioning' || data.created === true,
      })
      toast.success(data.created ? 'Workspace created.' : 'Welcome back.')
      goNext()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not create workspace.')
    } finally {
      setBusy(false)
    }
  }, [pending, busy, wsName, goNext])

  const useDifferentAccount = useCallback(() => {
    supabase?.auth.signOut().then(() => { setPending(null); setNotice(null) })
  }, [])

  return {
    // state
    next, busy, notice, setNotice, configured,
    pending, wsName, setWsName, recoveryReady,
    // actions
    signInGoogle, signInEmail, signUpEmail, sendReset, updatePassword,
    createWorkspace, useDifferentAccount,
  }
}
